import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MARKETING_VERSION,
  MARKETING_VERSION_V2,
  MARKETING_VERSION_V3,
} from '../config/marketingVersion.js';
import { SITE_MODE } from '../config/siteMode';
import {
  SEO_FULL_MODE,
  SEO_SITE_ORIGIN_FALLBACK,
  buildFaqSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
  buildWebsiteSchema,
  getCanonicalUrl,
  getHomepageMeta,
  getHomepageSocialCard,
  getHreflangAlternates,
  isHomepagePath,
  normalizeSeoLocale,
  normalizeSeoSiteMode,
  resolveSeoOrigin,
} from './seoMetadata';

const ROBOTS_CONTENT = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
const NOINDEX_ROBOTS_CONTENT = 'noindex, nofollow';

const JSON_LD_IDS = Object.freeze({
  organization: 'seo-jsonld-organization',
  website: 'seo-jsonld-website',
  software: 'seo-jsonld-software',
  faq: 'seo-jsonld-faq',
});

function upsertMeta({ name, property, content }) {
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement('meta');
    if (name) {
      tag.setAttribute('name', name);
    }
    if (property) {
      tag.setAttribute('property', property);
    }
    document.head.append(tag);
  }

  tag.setAttribute('content', String(content || ''));
}

function upsertCanonicalLink(href) {
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.append(tag);
  }

  tag.setAttribute('href', href);
}

function upsertHreflangLinks(alternates) {
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((tag) => tag.parentNode?.removeChild(tag));

  alternates.forEach((alternate) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', alternate.hrefLang);
    link.setAttribute('href', alternate.href);
    document.head.append(link);
  });
}

function upsertJsonLd(id, payload) {
  let script = document.head.querySelector(`script#${id}`);
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('id', id);
    script.setAttribute('type', 'application/ld+json');
    document.head.append(script);
  }

  script.textContent = JSON.stringify(payload);
}

function removeJsonLd(id) {
  const script = document.head.querySelector(`script#${id}`);
  script?.parentNode?.removeChild(script);
}

function getSeoOrigin() {
  const configuredOrigin = import.meta.env.VITE_PUBLIC_SITE_URL || SEO_SITE_ORIGIN_FALLBACK;
  return resolveSeoOrigin(configuredOrigin);
}

export function SeoHead({ noIndex = false }) {
  const location = useLocation();
  const { i18n, t } = useTranslation();

  const siteMode = normalizeSeoSiteMode(SITE_MODE);
  const locale = normalizeSeoLocale(i18n.resolvedLanguage || i18n.language);
  const homepageMeta = getHomepageMeta(locale, siteMode, MARKETING_VERSION);
  const socialCard = getHomepageSocialCard(siteMode, MARKETING_VERSION);
  const faqItems = useMemo(() => {
    let faqTranslationKey = 'faq.items';
    if (siteMode === SEO_FULL_MODE && MARKETING_VERSION === MARKETING_VERSION_V2) {
      faqTranslationKey = 'landingV2.faq.items';
    } else if (siteMode === SEO_FULL_MODE && MARKETING_VERSION === MARKETING_VERSION_V3) {
      faqTranslationKey = 'landingV3.faq.items';
    }

    const translated = t(faqTranslationKey, { returnObjects: true });
    return Array.isArray(translated) ? translated : [];
  }, [locale, siteMode, t]);
  const shouldPublishFaqSchema = siteMode === SEO_FULL_MODE;

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const origin = getSeoOrigin();
    const pathname = location.pathname || '/';
    const canonicalUrl = getCanonicalUrl({ origin, pathname, locale });
    const alternates = getHreflangAlternates({ origin, pathname });
    const ogImageUrl = new URL(socialCard.imagePath, origin).toString();

    document.documentElement.lang = locale;
    document.title = homepageMeta.title;

    upsertMeta({ name: 'description', content: homepageMeta.description });
    upsertMeta({ name: 'robots', content: noIndex ? NOINDEX_ROBOTS_CONTENT : ROBOTS_CONTENT });
    upsertCanonicalLink(canonicalUrl);
    upsertHreflangLinks(alternates);

    upsertMeta({ property: 'og:type', content: 'website' });
    upsertMeta({ property: 'og:title', content: homepageMeta.title });
    upsertMeta({ property: 'og:description', content: homepageMeta.description });
    upsertMeta({ property: 'og:url', content: canonicalUrl });
    upsertMeta({ property: 'og:image', content: ogImageUrl });
    upsertMeta({ property: 'og:image:alt', content: socialCard.imageAlt });
    upsertMeta({ property: 'og:site_name', content: 'cedrum' });
    upsertMeta({ property: 'og:locale', content: locale });

    upsertMeta({ name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta({ name: 'twitter:title', content: homepageMeta.title });
    upsertMeta({ name: 'twitter:description', content: homepageMeta.description });
    upsertMeta({ name: 'twitter:image', content: ogImageUrl });
    upsertMeta({ name: 'twitter:image:alt', content: socialCard.imageAlt });

    upsertJsonLd(JSON_LD_IDS.organization, buildOrganizationSchema({ origin }));
    upsertJsonLd(
      JSON_LD_IDS.website,
      buildWebsiteSchema({
        origin,
        locale,
        description: homepageMeta.description,
      }),
    );
    upsertJsonLd(
      JSON_LD_IDS.software,
      buildSoftwareApplicationSchema({
        origin,
        locale,
        description: homepageMeta.description,
      }),
    );

    if (shouldPublishFaqSchema && isHomepagePath(pathname)) {
      const faqSchema = buildFaqSchema({ faqItems });
      if (faqSchema) {
        upsertJsonLd(JSON_LD_IDS.faq, faqSchema);
      } else {
        removeJsonLd(JSON_LD_IDS.faq);
      }
    } else {
      removeJsonLd(JSON_LD_IDS.faq);
    }
  }, [
    faqItems,
    homepageMeta.description,
    homepageMeta.title,
    locale,
    location.pathname,
    noIndex,
    shouldPublishFaqSchema,
    socialCard.imageAlt,
    socialCard.imagePath,
  ]);

  return null;
}

export default SeoHead;
